/**
 * Breadcrumb utilities for consistent navigation patterns
 * Note: Home breadcrumb is automatically added by Breadcrumb component
 */

/**
 * Validates breadcrumb path structure and removes invalid entries
 */
export const validateBreadcrumbPath = (path) => {
  if (!Array.isArray(path)) return [];
  
  return path.filter(item => {
    // Must have a label
    if (!item || !item.label || typeof item.label !== 'string') return false;
    
    // Filter out Ciao/Home entries (handled automatically by Breadcrumb component)
    if (item.label.toLowerCase() === 'home' || 
        item.label.toLowerCase() === 'ciao' || 
        item.route === '/') return false;
    
    // Route must be string if provided
    if (item.route && typeof item.route !== 'string') return false;
    
    // Icon must be string if provided
    if (item.icon && typeof item.icon !== 'string') return false;
    
    return true;
  });
};

/**
 * Generate standardized breadcrumb paths for different page types
 * Does not include Home entry as it's automatically added
 */
export const generateBreadcrumbPath = (pageType, pageData = {}) => {
  const basePaths = {
    recipe: [
      { label: 'Ciao', route: '/', icon: 'fas fa-home' }
    ],
    person: [
      { label: 'Rossetti', route: '/family-tree', icon: 'fas fa-sitemap' }
    ],
    gallery: [
      { label: 'Galleries', route: '/', icon: 'fas fa-images' }
    ],
    familyStats: [
      { label: 'Rossetti', route: '/family-tree', icon: 'fas fa-sitemap' }
    ],
    wedding: [
      { label: 'Rossetti', route: '/family-tree', icon: 'fas fa-sitemap' }
    ],
    thanksgiving: [
      { label: 'Ciao', route: '/', icon: 'fas fa-home' }
    ],
    plymouth: [],
    rawrock: [],
    tomatoes: [],
    aprilTwelve: []
  };

  const path = basePaths[pageType] || [];
  
  // Add specific page breadcrumb if pageData provided
  if (pageData.title) {
    path.push({
      label: pageData.title,
      icon: pageData.icon
    });
  }

  return path;
};

/**
 * Common breadcrumb configurations for different page types
 */
export const breadcrumbConfigs = {
  recipes: {
    basePath: [{ label: 'Ciao', route: '/', icon: 'fas fa-home' }],
    getPath: (recipeTitle) => [
      ...breadcrumbConfigs.recipes.basePath,
      ...(recipeTitle ? [{ label: recipeTitle, icon: 'fas fa-utensils' }] : [])
    ]
  },
  
  familyTree: {
    basePath: [{ label: 'Rossetti', route: '/family-tree', icon: 'fas fa-sitemap' }],
    getPath: (personName) => [
      ...breadcrumbConfigs.familyTree.basePath,
      ...(personName ? [{ label: personName, icon: 'fas fa-user' }] : [])
    ]
  },
  
  galleries: {
    basePath: [{ label: 'Galleries', route: '/', icon: 'fas fa-images' }],
    getPath: (galleryName) => [
      ...breadcrumbConfigs.galleries.basePath,
      ...(galleryName ? [{ label: galleryName, icon: 'fas fa-camera' }] : [])
    ]
  },
  
  familyStats: {
    basePath: [
      { label: 'Stats', icon: 'fas fa-chart-bar' }
    ],
    getPath: () => breadcrumbConfigs.familyStats.basePath
  },
  
  wedding: {
    basePath: [
      { label: 'Wedding', icon: 'fas fa-heart' }
    ],
    getPath: () => breadcrumbConfigs.wedding.basePath
  },
  
  thanksgiving: {
    basePath: [
      { label: 'Ciao', route: '/', icon: 'fas fa-home' },
      { label: 'Thanksgiving', icon: 'fas fa-turkey' }
    ],
    getPath: () => breadcrumbConfigs.thanksgiving.basePath
  },
  
  plymouth: {
    basePath: [
      { label: 'Plymouth', icon: 'fas fa-ship' }
    ],
    getPath: () => breadcrumbConfigs.plymouth.basePath
  },
  
  rawrock: {
    basePath: [
      { label: 'Rawrock', icon: 'fas fa-mountain' }
    ],
    getPath: () => breadcrumbConfigs.rawrock.basePath
  },
  
  tomatoes: {
    basePath: [
      { label: 'Tomatoes', icon: 'fas fa-seedling' }
    ],
    getPath: () => breadcrumbConfigs.tomatoes.basePath
  },
  
  aprilTwelve: {
    basePath: [
      { label: 'April Twelve', icon: 'fas fa-calendar-day' }
    ],
    getPath: () => breadcrumbConfigs.aprilTwelve.basePath
  }
};

/**
 * Get page configuration for consistent page setup
 */
export const getPageConfig = (pageType, pageData = {}) => {
  const configs = {
    recipe: {
      icon: 'fas fa-utensils',
      breadcrumbPath: breadcrumbConfigs.recipes.getPath(pageData.title)
    },
    person: {
      icon: 'fas fa-user',
      breadcrumbPath: breadcrumbConfigs.familyTree.getPath(pageData.name)
    },
    gallery: {
      icon: 'fas fa-images',
      breadcrumbPath: breadcrumbConfigs.galleries.getPath(pageData.title)
    },
    familyStats: {
      icon: 'fas fa-chart-bar',
      breadcrumbPath: breadcrumbConfigs.familyStats.getPath()
    },
    wedding: {
      icon: 'fas fa-heart',
      breadcrumbPath: breadcrumbConfigs.wedding.getPath()
    },
    thanksgiving: {
      icon: 'fas fa-turkey',
      breadcrumbPath: breadcrumbConfigs.thanksgiving.getPath()
    },
    plymouth: {
      icon: 'fas fa-ship',
      breadcrumbPath: breadcrumbConfigs.plymouth.getPath()
    },
    rawrock: {
      icon: 'fas fa-mountain',
      breadcrumbPath: breadcrumbConfigs.rawrock.getPath()
    },
    tomatoes: {
      icon: 'fas fa-seedling',
      breadcrumbPath: breadcrumbConfigs.tomatoes.getPath()
    },
    aprilTwelve: {
      icon: 'fas fa-calendar-day',
      breadcrumbPath: breadcrumbConfigs.aprilTwelve.getPath()
    }
  };

  return {
    ...configs[pageType],
    ...pageData
  };
};